import {
    FETCH_PAYMENT_METHODS,
    FETCH_PAYMENT_METHODS_SUCCESS,
    FETCH_PAYMENT_METHODS_FAILED,
    UPDATE_WALLET_BALANCE,
    UPDATE_WALLET_BALANCE_SUCCESS,
    UPDATE_WALLET_BALANCE_FAILED,
    CLEAR_PAYMENT_MESSAGES,
    UPDATE_PAYMENT_METHOD
} from "../store/types";
import { firebase } from '../config/configureFirebase';
import { onValue, update, child, get, push } from "firebase/database";
import { RequestPushMsg } from "../other/NotificationFunctions";
import store from "../store/store";

export const fetchPaymentMethods = () => async (dispatch, getState) => {
    const { config, paymentSettingsRef } = firebase;
    const state = typeof getState === "function" ? getState() : (store ? store.getState() : {});

    dispatch({
        type: FETCH_PAYMENT_METHODS,
        payload: null
    });

    const profile = state.auth?.profile;
    const usertype = profile?.usertype;

    if (usertype === "admin") {
        onValue(paymentSettingsRef, snapshot => {
            if (snapshot.val()) {
                const data = snapshot.val();
                const arr = Object.keys(data).map(i => {
                    data[i].id = i;
                    data[i].name = data[i].name || i;
                    data[i].link = data[i].link || `/${i}_link`;
                    return data[i];
                });
                dispatch({
                    type: FETCH_PAYMENT_METHODS_SUCCESS,
                    payload: arr
                });
            } else {
                dispatch({
                    type: FETCH_PAYMENT_METHODS_FAILED,
                    payload: state.languagedata?.defaultLanguage?.no_provider_found || "No payment methods available"
                });
            }
        });
    } else {
        const settings = state.settingsdata?.settings;
        const projectId = firebase.config?.projectId || config?.projectId || "drive2go-c806b";
        const isBrowser = typeof window !== 'undefined' && window.location && typeof window.location.origin === 'string';
        const region = (typeof process !== "undefined" && (process.env?.REACT_APP_FUNCTIONS_REGION || process.env?.FUNCTIONS_REGION)) || firebase.config?.functionsRegion || "europe-west1";
        const functionsBase = (typeof process !== "undefined" && process.env?.REACT_APP_FUNCTIONS_URL)
            ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '')
            : `https://${region}-${projectId}.cloudfunctions.net`;
        const webBase = `https://${projectId}.web.app`;

        const primaryHost = (isBrowser && settings?.CompanyWebsite === window.location.origin)
            ? window.location.origin
            : functionsBase;

        const parseProviders = (responseJson) => {
            let arr = [];
            if (Array.isArray(responseJson)) {
                arr = responseJson.filter(p => p && p.active).map(p => ({ ...p, name: p.name || p.id, link: p.link || `/${p.id || p.name}_link` }));
            } else if (responseJson && typeof responseJson === "object") {
                for (let key in responseJson) {
                    if (responseJson[key] && responseJson[key].active) {
                        responseJson[key].id = key;
                        responseJson[key].name = responseJson[key].name || key;
                        responseJson[key].link = responseJson[key].link || `/${key}_link`;
                        arr.push(responseJson[key]);
                    }
                }
            }
            return arr;
        };

        const tryFetchProviders = async () => {
            try {
                const res = await fetch(`${primaryHost}/get_providers`, {
                    method: "GET",
                    headers: { "Content-Type": "application/json" }
                });
                if (res.ok) {
                    const json = await res.json();
                    const arr = parseProviders(json);
                    if (arr.length > 0) return arr;
                }
            } catch (_) {}

            if (primaryHost !== webBase) {
                try {
                    const res = await fetch(`${webBase}/get_providers`, {
                        method: "GET",
                        headers: { "Content-Type": "application/json" }
                    });
                    if (res.ok) {
                        const json = await res.json();
                        const arr = parseProviders(json);
                        if (arr.length > 0) return arr;
                    }
                } catch (_) {}
            }

            return null;
        };

        tryFetchProviders().then(arr => {
            if (arr && arr.length > 0) {
                dispatch({
                    type: FETCH_PAYMENT_METHODS_SUCCESS,
                    payload: arr
                });
            } else {
                if (paymentSettingsRef && usertype === "admin") {
                    onValue(paymentSettingsRef, snapshot => {
                        if (snapshot.val()) {
                            const data = snapshot.val();
                            const fallbackArr = Object.keys(data)
                                .map(i => { data[i].id = i; data[i].name = data[i].name || i; data[i].link = data[i].link || `/${i}_link`; return data[i]; })
                                .filter(p => p && p.active);
                            if (fallbackArr.length > 0) {
                                dispatch({
                                    type: FETCH_PAYMENT_METHODS_SUCCESS,
                                    payload: fallbackArr
                                });
                                return;
                            }
                        }
                        const defaultActiveProviders = [
                            { id: "stripe", name: "stripe", active: true, link: "/stripe_link" },
                            { id: "paypal", name: "paypal", active: true, link: "/paypal_link" },
                            { id: "revolut", name: "revolut", active: true, link: "/revolut_link" }
                        ];
                        dispatch({
                            type: FETCH_PAYMENT_METHODS_SUCCESS,
                            payload: defaultActiveProviders
                        });
                    }, { onlyOnce: true });
                } else {
                    const defaultActiveProviders = [
                        { id: "stripe", name: "stripe", active: true, link: "/stripe_link" },
                        { id: "paypal", name: "paypal", active: true, link: "/paypal_link" },
                        { id: "revolut", name: "revolut", active: true, link: "/revolut_link" }
                    ];
                    dispatch({
                        type: FETCH_PAYMENT_METHODS_SUCCESS,
                        payload: defaultActiveProviders
                    });
                }
            }
        });
    }
};

export const updateWalletBalance = (balance, uid) => (dispatch) => {
    const {
        singleUserRef
    } = firebase;

    dispatch({
        type: UPDATE_WALLET_BALANCE,
        payload: null
    });

    update(singleUserRef(uid), { walletBalance: balance }).then(() => {
        dispatch({
            type: UPDATE_WALLET_BALANCE_SUCCESS,
            payload: null
        });
    }).catch(error => {
        dispatch({
            type: UPDATE_WALLET_BALANCE_FAILED,
            payload: error.code
        });
    });
};

export const clearPaymentMessages = () => (dispatch) => {
    dispatch({
        type: CLEAR_PAYMENT_MESSAGES,
        payload: null
    });
};

export const updatePaymentMethod = (paymentMethod) => (dispatch) => {
    const {
        paymentSettingsRef
    } = firebase;

    dispatch({
        type: UPDATE_PAYMENT_METHOD,
        payload: paymentMethod
    });

    if (paymentMethod.id) {
        const payload = {
            active: !!paymentMethod.active,
            mode: paymentMethod.mode || "test",
            keys: paymentMethod.keys || { test: {}, live: {} }
        };
        update(child(paymentSettingsRef, paymentMethod.id), payload);
    }
};

export const addToWallet = (uid, amount) => async (dispatch) => {
    const { walletHistoryRef, singleUserRef, settingsRef } = firebase;

    dispatch({
        type: UPDATE_WALLET_BALANCE,
        payload: null,
    });

    try {
        const settingsdata = await get(settingsRef);
        const settings = settingsdata ? settingsdata.val() : null;

        onValue(
            singleUserRef(uid),
            (snapshot) => {
                if (snapshot.val()) {
                    const rawBalance = snapshot.val().walletBalance;
                    let currentBalance = rawBalance !== undefined && rawBalance !== null ? parseFloat(rawBalance) : 0;
                    if (isNaN(currentBalance)) currentBalance = 0;
                    const pushToken = snapshot.val().pushToken;
                    const decimalPlaces = settings && settings.decimal !== undefined ? settings.decimal : 2;
                    let walletBalance = parseFloat(
                        (currentBalance + parseFloat(amount)).toFixed(decimalPlaces)
                    );
                    let details = {
                        type: "Credit",
                        amount: parseFloat(amount),
                        date: new Date().getTime(),
                        txRef: "AdminCredit",
                    };
                    update(singleUserRef(uid), { walletBalance: walletBalance })
                        .then(() => {
                            push(walletHistoryRef(uid), details)
                                .then(() => {
                                    dispatch({
                                        type: UPDATE_WALLET_BALANCE_SUCCESS,
                                        payload: null,
                                    });
                                })
                                .catch((error) => {
                                    dispatch({
                                        type: UPDATE_WALLET_BALANCE_FAILED,
                                        payload: error.code + ": " + error.message,
                                    });
                                });
                            if (pushToken) {
                                RequestPushMsg(pushToken, {
                                    title:
                                        store.getState().languagedata?.defaultLanguage?.notification_title || "Notification",
                                    msg:
                                        store.getState().languagedata?.defaultLanguage?.wallet_updated || "Wallet Updated",
                                    screen: "Wallet",
                                });
                            }
                        })
                        .catch((error) => {
                            dispatch({
                                type: UPDATE_WALLET_BALANCE_FAILED,
                                payload: error.code + ": " + error.message,
                            });
                        });
                }
            },
            { onlyOnce: true }
        );
    } catch (err) {
        dispatch({
            type: UPDATE_WALLET_BALANCE_FAILED,
            payload: err.toString(),
        });
    }
};

export const withdrawBalance = (profile, amount) => async (dispatch) => {
    const { withdrawRef } = firebase;

    dispatch({
        type: UPDATE_WALLET_BALANCE,
        payload: null,
    });

    push(withdrawRef, {
        uid: profile.uid,
        name: (profile.firstName || "") + " " + (profile.lastName || ""),
        amount: parseFloat(amount),
        date: new Date().getTime(),
        bankName: profile.bankName ? profile.bankName : "",
        bankCode: profile.bankCode ? profile.bankCode : "",
        bankAccount: profile.bankAccount ? profile.bankAccount : "",
        processed: false,
    });
};

export const deductWalletForBooking = (uid, amount, bookingId) => async () => {
    const { singleUserRef, walletHistoryRef } = firebase;

    try {
        const userSnap = await get(singleUserRef(uid));
        if (userSnap.exists()) {
            const raw = userSnap.val().walletBalance;
            let currentBalance = raw !== undefined && raw !== null ? parseFloat(raw) : 0;
            if (isNaN(currentBalance)) currentBalance = 0;

            const newBalance = parseFloat((currentBalance - parseFloat(amount)).toFixed(2));
            await update(singleUserRef(uid), { walletBalance: newBalance });

            await push(walletHistoryRef(uid), {
                type: "Debit",
                amount: parseFloat(amount),
                date: new Date().getTime(),
                txRef: "BookingPayment",
                bookingId: bookingId || null
            });
        }
    } catch (e) {
        console.error("Failed to deduct wallet for booking:", e);
    }
};

/**
 * cancelWalletCredit — Admin cancels an unused AdminCredit entry.
 * @param {string} uid - The user ID
 * @param {string} transactionId - The wallet history entry ID to cancel
 * @param {number} amount - The credit amount to reverse
 */
export const cancelWalletCredit = (uid, transactionId, amount) => async (dispatch) => {
    const { singleUserRef, walletHistoryRef } = firebase;

    dispatch({
        type: UPDATE_WALLET_BALANCE,
        payload: null,
    });

    try {
        // 1. Read current balance
        const userSnap = await get(singleUserRef(uid));
        if (!userSnap.exists()) {
            dispatch({ type: UPDATE_WALLET_BALANCE_FAILED, payload: "User not found" });
            return { success: false, reason: "user_not_found" };
        }

        const rawBalance = userSnap.val().walletBalance;
        let currentBalance = rawBalance !== undefined && rawBalance !== null ? parseFloat(rawBalance) : 0;
        if (isNaN(currentBalance)) currentBalance = 0;

        const creditAmount = parseFloat(amount);

        // 2. Check positive balance
        if (currentBalance <= 0) {
            dispatch({ type: UPDATE_WALLET_BALANCE_FAILED, payload: "Insufficient balance" });
            return { success: false, reason: "insufficient_balance" };
        }

        const deductAmount = parseFloat(Math.min(currentBalance, creditAmount).toFixed(2));

        // 3. Deduct from walletBalance
        const settingsRef = firebase.settingsRef;
        const settingsSnap = await get(settingsRef);
        const settings = settingsSnap ? settingsSnap.val() : null;
        const decimalPlaces = settings && settings.decimal !== undefined ? settings.decimal : 2;
        const newBalance = parseFloat(Math.max(0, currentBalance - deductAmount).toFixed(decimalPlaces));
        await update(singleUserRef(uid), { walletBalance: newBalance });

        // 4. Mark original entry as cancelled
        await update(child(walletHistoryRef(uid), transactionId), {
            cancelled: true,
            cancelledAmount: deductAmount,
        });

        // 5. Create a "Cancelled" entry in walletHistory
        await push(walletHistoryRef(uid), {
            type: "Cancelled",
            amount: deductAmount,
            date: new Date().getTime(),
            txRef: "AdminCreditCancelled",
            originalTransactionId: transactionId,
        });

        dispatch({
            type: UPDATE_WALLET_BALANCE_SUCCESS,
            payload: null,
        });

        return { success: true, newBalance, deductAmount };
    } catch (err) {
        dispatch({
            type: UPDATE_WALLET_BALANCE_FAILED,
            payload: err.toString(),
        });
        return { success: false, reason: err.toString() };
    }
};